import { useMemo } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/feature/ComicCard';
import { getComic, categoriesMap, comics } from '@/mocks/comics';

export default function ComicDetailPage() {
  const { id = '' } = useParams();
  const comic = getComic(id);

  const related = useMemo(
    () => comics.filter((c) => c.id !== id && c.category === comic?.category).slice(0, 5),
    [id, comic],
  );

  if (!comic) {
    return <Navigate to="/" replace />;
  }

  const cat = categoriesMap[comic.category];
  const chapterList = Array.from({ length: comic.chapters }, (_, i) => comic.chapters - i);

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${comic.title} - ${comic.tags.join('、')}漫画`,
    description: comic.description,
    image: comic.cover,
    author: { '@type': 'Person', name: comic.author },
    dateModified: new Date().toISOString().split('T')[0],
    mainEntityOfPage: `https://dailycartoon.github.io/comic/${comic.id}`,
  };

  return (
    <div className="min-h-screen bg-background-50 text-foreground-950">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />

      <Header />

      <main>
        <section className="border-b border-background-200/60 bg-background-100">
          <div className="mx-auto max-w-7xl px-4 py-8 md:px-6">
            <nav aria-label="面包屑" className="mb-5 text-xs text-foreground-500">
              <ol className="flex flex-wrap items-center gap-1">
                <li><Link to="/" className="transition-colors hover:text-primary-400">首页</Link></li>
                <li><i className="ri-arrow-right-s-line" aria-hidden="true"></i></li>
                <li><Link to={`/category/${cat.slug}`} className="transition-colors hover:text-primary-400">{cat.name}漫画</Link></li>
                <li><i className="ri-arrow-right-s-line" aria-hidden="true"></i></li>
                <li className="text-foreground-400">{comic.title}</li>
              </ol>
            </nav>

            <div className="flex flex-col gap-6 md:flex-row">
              <div className="w-40 shrink-0 overflow-hidden rounded-xl border border-background-200 md:w-52">
                <img
                  src={comic.cover}
                  alt={`${comic.title} ${comic.tags.join('、')}漫画封面`}
                  className="aspect-[3/4] w-full object-cover object-top"
                />
              </div>

              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded bg-primary-500 px-2.5 py-1 text-xs text-background-50">{cat.name}</span>
                  <span className={`rounded px-2.5 py-1 text-xs ${comic.status === '已完结' ? 'bg-secondary-200 text-secondary-950' : 'bg-accent-500 text-background-50'}`}>
                    {comic.status}
                  </span>
                </div>

                <h1 className="mt-3 font-heading text-3xl text-foreground-950">{comic.title}</h1>
                <p className="mt-1 text-sm text-foreground-600">作者 · {comic.author}</p>

                <div className="mt-4 flex flex-wrap gap-2">
                  {comic.tags.map((t) => (
                    <span key={t} className="rounded-full bg-secondary-100 px-3 py-1 text-xs text-foreground-600">
                      {t}
                    </span>
                  ))}
                </div>

                <div className="mt-5 grid max-w-md grid-cols-3 gap-4">
                  <div>
                    <div className="font-heading text-xl text-accent-400">{comic.rating}</div>
                    <div className="mt-0.5 text-xs text-foreground-500">读者评分</div>
                  </div>
                  <div>
                    <div className="font-heading text-xl text-foreground-950">{comic.chapters} 话</div>
                    <div className="mt-0.5 text-xs text-foreground-500">已更新</div>
                  </div>
                  <div>
                    <div className="font-heading text-xl text-foreground-950">{comic.views}</div>
                    <div className="mt-0.5 text-xs text-foreground-500">累计人气</div>
                  </div>
                </div>

                <p className="mt-5 max-w-2xl text-sm leading-relaxed text-foreground-600">{comic.description}</p>

                <div className="mt-6 flex flex-wrap gap-3">
                  <button type="button" className="flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-600">
                    <i className="ri-play-line" aria-hidden="true"></i> 立即阅读
                  </button>
                  <button type="button" className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-5 py-2.5 text-sm text-foreground-950 transition-colors hover:border-primary-400/50">
                    <i className="ri-bookmark-line" aria-hidden="true"></i> 加入追更
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-4 py-10 md:px-6">
          <div className="mb-5 flex items-center justify-between">
            <h2 className="font-heading text-xl text-foreground-950">章节列表</h2>
            <span className="text-xs text-foreground-500">共 {comic.chapters} 话</span>
          </div>
          <div className="grid max-h-[420px] grid-cols-2 gap-2 overflow-y-auto pr-2 scrollbar-thin sm:grid-cols-3 lg:grid-cols-4">
            {chapterList.map((n) => (
              <button
                key={n}
                type="button"
                className={`whitespace-nowrap rounded-md border px-3 py-2.5 text-left text-sm transition-colors ${
                  n === comic.chapters
                    ? 'border-primary-400/60 bg-primary-500/10 text-primary-300'
                    : 'border-background-200 bg-background-100 text-foreground-600 hover:border-primary-400/40 hover:text-foreground-950'
                }`}
              >
                第{n}话
              </button>
            ))}
          </div>
        </section>

        {related.length > 0 && (
          <section className="border-t border-background-200/60 bg-background-100">
            <div className="mx-auto max-w-7xl px-4 py-12 md:px-6">
              <h2 className="mb-6 font-heading text-xl text-foreground-950">同类推荐</h2>
              <div data-product-shop className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
                {related.map((c) => (
                  <ComicCard key={c.id} comic={c} />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </div>
  );
}